import { FiRemoteDataSource } from './fi-remote-data-source';
import { ChangeDetectorRef, Directive, inject, Injector, Input, signal } from '@angular/core';
import { FiStateService } from '../fi-state-proxy/fi-state.proxy.service';
import { MatSortDirection, ObjectTypes, SideListOptionType } from '../types';
import {
    IItemsListParameterBase,
    IItemsListPublicMethods,
    IItemsListSort,
} from '@fi-shared/fi-items-list/fi-items-list';
import { Sort } from '@angular/material/sort';
import { takeUntil } from 'rxjs/operators';
import { AppTextService } from '@fi-shared/app-text';
import { FiItemsListHelperService } from '@fi-shared/fi-items-list/fi-items-list-helper.service';
import { getObjectTypeNameTextId, isDefined } from '@fi-shared/utils';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';
import { UserPreferencesService } from '@fi-shared/fi-shared-services/user-preferences.service';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

@Directive()
export abstract class FiRemoteStateFullDataSource<T> extends FiRemoteDataSource<T> {
    @Input() extendedSearchMode: boolean;

    protected cdr = inject(ChangeDetectorRef);
    protected fiStateService = inject(FiStateService);
    protected appTextService = inject(AppTextService);
    protected itemsListHelperService = inject(FiItemsListHelperService);
    protected fiDialogsService = inject(FiDialogsService);
    protected fiLoading = inject(LoadingService);
    protected snackBar = inject(FiSnackBarService);
    protected detectDevicesService = inject(DetectDevicesService);
    private sharedActionsBaseService = inject(SharedActionsBaseService);
    protected userPreferencesService = inject(UserPreferencesService);
    protected userDataService = inject(FiUserDataService);

    filterType: SideListOptionType;
    defaultFilterTypeId: SideListOptionType;
    sortColumn: string;
    protected defaultFilterType: SideListOptionType = SideListOptionType.SharedWithMe;
    protected sortDirection: boolean;
    protected initialOffset: number;
    abstract defaultSortColumnName: string;
    abstract defaultSortDirection: boolean;
    protected skipInitSelectedSort = true; // don't save/restore selected list sort
    abstract objectType: ObjectTypes;
    private mobileAppMode: boolean;
    abstract itemsListType: FiItemsListType;
    protected currentListDisplayType: FiDisplayType;

    protected isMobileFilterShown = signal<boolean>(false);


    selectedTagId: number;

    protected FiStateNames = FiStateNames;

    constructor(injector: Injector) {
        super(injector);

        setTimeout(() => {
            this.setSortData();
            this.parseStateParams();
            this.reloadData();
            this.subscribeOnPreferredBusinessUnitChange();
            this.items$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
                this.cdr.detectChanges();
            });
        });
    }

    protected setSortData() {
        const userSortData: IItemsListSort = !this.skipInitSelectedSort
            ? this.userPreferencesService.getListSort(this.itemsListType)
            : undefined;
        this.sortColumn = userSortData?.ColumnName ?? this.defaultSortColumnName;
        this.sortDirection = userSortData?.SortDirection ?? this.defaultSortDirection;
    }

    private parseStateParams() {
        const stateParams = this.itemsListHelperService.parseStateParams({
            extendedSearchMode: this.extendedSearchMode,
            mobileAppMode: this.mobileAppMode,
            defaultFilterType: this.defaultFilterType,
        });
        if (isDefined(stateParams.filterType)) {
            this.filterType = stateParams.filterType;
        }
        if (isDefined(stateParams.initialOffset)) {
            this.initialOffset = stateParams.initialOffset;
        }
    }

    private updateStateParams() {
        this.itemsListHelperService.updateStateParams({
            extendedSearchMode: this.extendedSearchMode,
            mobileAppMode: this.mobileAppMode,
            filterType: this.filterType,
            sortColumn: this.sortColumn,
            sortDirection: this.sortDirection,
            selectedTagId: this.selectedTagId,
        });
    }

    toggleFilterOverlay() {
        this.isMobileFilterShown.update(value => !value);
    }

    onSideListOptionsChanged(filterType: SideListOptionType, args?: { skipUpdateStateParams?: boolean }) {
        if (!args) {
            args = {};
        }
        this.filterType = filterType;
        this.reloadData();
        if (!args.skipUpdateStateParams) {
            this.updateStateParams();
        }
    }

    getMatSortDirection(): MatSortDirection {
        return this.sortDirection ? MatSortDirection.Descending : MatSortDirection.Ascending;
    }

    onSortChanged($event: Sort) {
        const selectedSort = this.getSelectedSort();
        if (selectedSort) {
            this.sortColumn = selectedSort.ColumnName;
            this.sortDirection = selectedSort.SortDirection;
        }
        if (!this.extendedSearchMode && !this.skipInitSelectedSort) {
            this.userPreferencesService.saveListSort(this.itemsListType, selectedSort);
        }
        this.updateStateParams();
        super.onSortChanged($event);
    }

    doSort(sortColumn: string, sortDirection: boolean) {
        this.matSort.sort({ id: sortColumn, start: sortDirection ? 'desc' : 'asc', disableClear: false });
    }

    protected getItemsListParameter(loadArgs: ILoadDataArgs): IItemsListParameterBase {
        if (!loadArgs.fetchParams) {
            loadArgs.fetchParams = {
                offset: 0,
                numberOfRecordsToTake: this.numberOfRecordsToTake,
            };
        }
        let offset: number, numberOfRecordsToTake: number;

        if (this.initialOffset && !loadArgs.fetchParams.offset) {
            // offset from state received, should load data from beginning
            offset = 0;
            numberOfRecordsToTake = this.initialOffset + loadArgs.fetchParams.numberOfRecordsToTake;
        } else {
            offset = loadArgs.fetchParams.offset;
            numberOfRecordsToTake = loadArgs.fetchParams.numberOfRecordsToTake;
        }

        let parameter: IItemsListParameterBase = {
            SelectedSort: {
                ColumnName: this.sortColumn,
                SortDirection: this.sortDirection,
            },
            Names: [],
            Ids: [],
            FilterType: this.filterType,
            Offset: offset,
            NumberOfRecordsToTake: numberOfRecordsToTake,
        };
        this.mergeLocalSearchParameter(parameter);
        return parameter;
    }

    //region Deleting
    protected getDeleteConfirmMessageText(isSingle: boolean = false) {
        const isPlural = isSingle ? false : this.selectedItems.length !== 1;
        const itemName = this.appTextService.get(getObjectTypeNameTextId(this.objectType, isPlural)).toLowerCase();
        const selectedText = this.itemsListHelperService.getSelectedTextByObjectType(this.objectType, isPlural);
        const deleteConfirmMessage = this.appTextService.get('delete.confirm.items', {
            '{0}': itemName,
            '{1}': selectedText,
        });
        return deleteConfirmMessage;
    }

    protected onDeleteSingleItem(item: T): void {
        this.showConfirmationDialog(true).onYesButtonClick(() => {
            this.doDeleteItems([item]);
        });
    }

    protected onDeleteSelectedItemsClick() {
        const itemsToDelete = this.selectedItems().slice(0);
        this.showConfirmationDialog().onYesButtonClick(() => this.doDeleteItems(itemsToDelete));
    }

    private showConfirmationDialog(isSingle: boolean = false) {
        return this.fiDialogsService.confirm({
            headerText: this.appTextService.get('common.warning'),
            messageText: this.getDeleteConfirmMessageText(isSingle),
            yesButtonText: this.appTextService.get('common.delete'),
        });
    }

    protected doDeleteItems(itemsToDelete: T[]) {
        this.deleteFn(itemsToDelete)
            .then(() => {
                this.removeData(itemsToDelete);
                this.showDeleteUndoSnackbar(itemsToDelete);
            })
            .catch((errorResponse) => {
                if (
                    errorResponse &&
                    errorResponse.error &&
                    errorResponse.error.Data &&
                    errorResponse.error.Data.ObjectId &&
                    errorResponse.error.Data.ObjectId
                ) {
                    const failedIds: number[] = errorResponse.error.Data.ObjectId;
                    const deletedItems = itemsToDelete.filter(
                        (itemToDelete) => failedIds.indexOf(itemToDelete['Id']) === -1,
                    );
                    this.removeData(deletedItems);
                    this.showDeleteUndoSnackbar(deletedItems);
                }
            });
    }

    private showDeleteUndoSnackbar(deletedItems: T[]) {
        if (!deletedItems.length) return;
        const isRestoreEnabled = false; // todo
        const snackbarTextMessage =
            deletedItems.length +
            ' ' +
            this.appTextService.get(getObjectTypeNameTextId(this.objectType, deletedItems.length !== 1)).toLowerCase() +
            ' ' +
            this.appTextService.get(isRestoreEnabled ? 'history.moved_to_trash' : 'history.deleted').toLowerCase();
        const snackbarButtonText = this.appTextService.get(isRestoreEnabled ? 'common.undo' : 'common.dismiss');

        this.snackBar
            .showSnackbar({
                textMessage: snackbarTextMessage,
                buttonText: snackbarButtonText,
            })
            .onAction()
            .subscribe(() => {
                if (isRestoreEnabled) {
                    // todo
                }
            });
    }

    protected deleteFn(itemsToDelete: T[]): IFiHttpPromise<any> {
        throw 'FI: deleteFn not implemented';
    }
    //endregion

    onUserClick($event: MouseEvent, userId: number) {
        $event.preventDefault();
        this.sharedActionsBaseService.openUserViewPopup(userId);
    }

    selectTagOption(tagId: number) {
        // todo: update state params
        if (this.isTagSelected(tagId)) {
            // reset to filterType;
            this.selectedTagId = undefined;
            this.filterType = this.defaultFilterTypeId;
        } else {
            // set tag as filterType;
            this.selectedTagId = tagId;
        }

        this.reloadData();
        this.updateStateParams();
    }
    isTagSelected(tagId: number) {
        return this.selectedTagId === tagId;
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    protected subscribeOnListDisplayTypeChange() {
        this.userPreferencesService
            .onListDisplayTypeUpdate$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((value) => {
                if (value === this.itemsListType) this.setCurrentListDisplayType();
            });
    }

    protected subscribeOnPreferredBusinessUnitChange() {
        this.userDataService.onPreferredBusinessUnitChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.reloadData();
        });
    }

    //protected subscribeOn

    protected setCurrentListDisplayType() {
        this.currentListDisplayType = this.userPreferencesService.getListDisplayType(this.itemsListType);
        this.cdr.markForCheck();
    }

    protected isDisplayTypeListGrid(): boolean {
        return this.currentListDisplayType === FiDisplayType.Grid;
    }

    getPublicMethods(): IItemsListPublicMethods<T> {
        return {
            getFilterType: () => this.filterType,
            reloadData: () => this.reloadData(),
            removeData: (items) => {
                this.removeData(items);
                this.cdr.detectChanges();
            },
        };
    }
}
